import { select } from 'd3-selection'
import { format } from 'd3-format'
import * as Legend from '../legend'
import { executeForAllInsets } from '../../core/utils'
import { appendPatternFillLegend } from '../legend-pattern-fill'
import { formatSizeLabel, getMaxDomainPrecision } from '../legend-utils'
//types
/** @typedef {import('../../types/core/MapInstance').MapInstance} MapInstance */

/**
 * A legend for bar chart maps.
 *
 * Adapts automatically to the map's `barType`:
 *
 * **'stacked'** — shows horizontal example bars at min/mid/max widths, labeled
 * with the corresponding total values, followed by a colour swatch per category.
 *
 * **'grouped'** — shows vertical example bars at min/mid/max heights, labeled
 * with the corresponding individual category values, followed by a colour swatch
 * per category. The bars grow upward from a baseline, matching the map's own
 * visual language.
 *
 * @param {Object} map - The bar chart map instance
 * @param {Object} config - Legend configuration overrides
 */
export const legend = function (map, config) {
    const out = Legend.legend(map)

    // ── Size legend config ───────────────────────────────────────────────────
    out.sizeLegend = {
        title: null,
        titlePadding: 10,
        offsetX: 10,
        values: null, // custom values; null → auto [min, mid, max]
        valueCount: 3, // grouped mode only, when values is null; 2 → auto [min, max], 3 → auto [min, mid, max]
        labelFormatter: undefined,
        noData: false,
        noDataText: 'No data',
        barGap: null, // grouped mode only; gap between example bars. null → falls back to the map's own barSettings.groupGap
    }

    // ── Width legend config (grouped mode + data-driven width) ─────────────
    out.widthLegend = {
        title: 'Bar width',
        titlePadding: 10,
        marginTop: 20,
        barHeight: 8,
        offsetX: 10,
        values: null, // custom values; null -> auto [max, mid, 10% max]
        labelFormatter: undefined,
    }

    // ── Colour legend config ─────────────────────────────────────────────────
    out.colorLegend = {
        title: null,
        titlePadding: 10,
        marginTop: 20,
        labelOffsets: { x: 5, y: 5 },
        shapeWidth: 25,
        shapeHeight: 20,
        shapePadding: 1,
        noData: true,
        noDataText: 'No data',
    }

    // ── Config override ──────────────────────────────────────────────────────
    if (config) {
        for (const key in config) {
            if (key === 'colorLegend' || key === 'sizeLegend' || key === 'widthLegend') {
                if (config[key] === false) {
                    out[key] = false
                } else {
                    for (const p in out[key]) {
                        if (config[key][p] !== undefined) out[key][p] = config[key][p]
                    }
                }
            } else {
                out[key] = config[key]
            }
        }
    }

    // ── Update ───────────────────────────────────────────────────────────────

    //@override
    out.update = function () {
        out.updateConfig()
        out.updateContainer()

        const lgg = out.lgg
        lgg.selectAll('*').remove()
        out.makeBackgroundBox()

        if (out.title) out.addTitle()
        if (out.subtitle) out.addSubtitle()

        const baseY = out.getBaseY()
        const baseX = out.getBaseX()

        // Size legend — branches on barType
        if (map.classifierSize_) {
            out._sizeLegendContainer = lgg.append('g').attr('class', 'em-bar-size-legend').attr('transform', `translate(${baseX}, ${baseY})`)

            if (map.barSettings_.type === 'grouped') {
                drawGroupedSizeLegend(
                    out,
                    out._sizeLegendContainer,
                    out.sizeLegend.values,
                    map.classifierSize_,
                    out.sizeLegend.title,
                    out.sizeLegend.titlePadding,
                    map.barSettings_.groupWidth,
                    map.barSettings_.groupGap,
                    map.catColors_
                )
            } else {
                drawStackedSizeLegend(
                    out,
                    out._sizeLegendContainer,
                    out.sizeLegend.values,
                    map.classifierSize_,
                    out.sizeLegend.title,
                    out.sizeLegend.titlePadding,
                    map.barSettings_.height,
                    map.catColors_
                )
            }
        }

        if (map.barSettings_?.type === 'grouped' && map.classifierWidth_ && out.widthLegend !== false) {
            const y = baseY + getLegendBlockHeight(out._sizeLegendContainer) + out.widthLegend.marginTop
            out._widthLegendContainer = lgg.append('g').attr('class', 'em-bar-width-legend').attr('transform', `translate(${baseX}, ${y})`)

            drawGroupedWidthLegend(
                out,
                out._widthLegendContainer,
                out.widthLegend.values,
                map.classifierWidth_,
                out.widthLegend.title,
                out.widthLegend.titlePadding,
                out.widthLegend.barHeight,
                out.widthLegend.offsetX
            )
        }

        buildColorLegend(out, baseX, baseY)

        if (map.patternFill_) {
            const legendHeight = out.lgg.node().getBBox().height
            const patternContainer = out.lgg
                .append('g')
                .attr('class', 'pattern-fill-legend')
                .attr('transform', `translate(${baseX}, ${legendHeight + 15})`)
            appendPatternFillLegend(out, patternContainer)
        }

        out.setBoxDimension()
    }

    // ── Grouped width legend ────────────────────────────────────────────────

    /**
     * Draw horizontal bars at representative width-channel values.
     * Width encodes the value; height is fixed for visual comparison.
     */
    function drawGroupedWidthLegend(legend, container, values, classifierWidth, title, titlePadding, barHeight, offsetX) {
        const domain = classifierWidth.domain()
        const maxDomain = domain?.[1] ?? 1
        const legendValues = values || [maxDomain, Math.round(maxDomain / 2), Math.max(1, Math.round(maxDomain * 0.1))]
        const sortedValues = [...legendValues].sort((a, b) => b - a)
        const dec = typeof legend.widthLegend?.labelFormatter === 'function' ? undefined : getMaxDomainPrecision(classifierWidth)

        const h = barHeight ?? 8
        const x0 = offsetX ?? 0
        const maxBarWidth = classifierWidth(sortedValues[0])
        const rowPadding = 4

        let y = 0
        if (title) {
            container.append('text').attr('class', 'em-size-legend-title').attr('x', 0).attr('y', y).attr('dominant-baseline', 'hanging').text(title)
            y += legend.titleFontSize + titlePadding
        }

        for (const val of sortedValues) {
            const w = classifierWidth(val)
            container
                .append('rect')
                .attr('x', x0)
                .attr('y', y)
                .attr('width', w)
                .attr('height', h)
                .attr('rx', legend.map?.barSettings_?.cornerRadius ?? 1)
                .attr('ry', legend.map?.barSettings_?.cornerRadius ?? 1)
                .attr('fill', '#7f7f7f')
                .attr('stroke', legend.map?.barSettings_?.strokeFill || 'white')
                .attr('stroke-width', (legend.map?.barSettings_?.strokeWidth ?? 0.3) + 'px')

            container
                .append('text')
                .attr('class', 'em-legend-label')
                .attr('x', x0 + maxBarWidth + 8)
                .attr('y', y + h / 2)
                .attr('dominant-baseline', 'middle')
                .text(formatSizeLabel(val, legend.widthLegend?.labelFormatter ?? dec))

            y += h + rowPadding
        }
    }

    // ── Stacked size legend ───────────────────────────────────────────────────

    /**
     * Draw horizontal example bars at representative total values.
     * Bar width encodes the total; segments are equal-width colour slices
     * matching the category colours (shape hint, not real proportions).
     *
     * @param {Object} legend
     * @param {Object} container
     * @param {Array|null} values - Custom values; null → auto [min, mid, max]
     * @param {Function} classifierSize - scale: total → bar width px
     * @param {string|null} title
     * @param {number} titlePadding
     * @param {number} barHeight
     * @param {Object} catColors
     */
    function drawStackedSizeLegend(legend, container, values, classifierSize, title, titlePadding, barHeight, catColors) {
        const domain = classifierSize.domain()
        const legendValues = values || [domain[0], Math.round((domain[0] + domain[1]) / 2), domain[1]]
        const sortedValues = [...legendValues].sort((a, b) => b - a) // largest first
        const dec = typeof legend.sizeLegend?.labelFormatter === 'function' ? undefined : getMaxDomainPrecision(classifierSize)
        const offsetX = legend.sizeLegend?.offsetX ?? 0

        let y = 0

        if (title) {
            container.append('text').attr('class', 'em-size-legend-title').attr('x', 0).attr('y', y).attr('dominant-baseline', 'hanging').text(title)
            y += legend.titleFontSize + titlePadding
        }

        const maxBarWidth = classifierSize(sortedValues[0])
        const colors = catColors ? Object.values(catColors) : ['#7f7f7f']
        const h = barHeight || 8
        const padding = 2

        for (const val of sortedValues) {
            const barWidth = classifierSize(val)
            const segCount = colors.length || 1
            const segWidth = barWidth / segCount

            for (let i = 0; i < segCount; i++) {
                container
                    .append('rect')
                    .attr('x', offsetX + i * segWidth)
                    .attr('y', y)
                    .attr('width', segWidth)
                    .attr('height', h)
                    .attr('rx', 1)
                    .attr('ry', 1)
                    .attr('fill', colors[i] || '#7f7f7f')
                    .attr('stroke', 'white')
                    .attr('stroke-width', 0.3)
            }

            container
                .append('text')
                .attr('class', 'em-legend-label')
                .attr('x', offsetX + maxBarWidth + 8)
                .attr('y', y + h / 2)
                .attr('dominant-baseline', 'middle')
                .text(formatSizeLabel(val, legend.sizeLegend?.labelFormatter ?? dec))

            y += h + padding
        }
    }

    // ── Grouped size legend ───────────────────────────────────────────────────

    /**
     * Draw vertical example bars at representative individual category values.
     * Bars grow upward from a baseline; each example uses the first category
     * colour. Labels appear below the baseline, centered under each bar.
     *
     * Three bars are drawn side by side (largest on the left), spaced with
     * the same barGroupGap as the map, so the example clusters look like the
     * actual chart symbols.
     *
     *     ┌──┐
     *     │  │ ┌──┐
     *     │  │ │  │ ┌──┐
     *  ───┴──┴─┴──┴─┴──┴───
     *    100K  50K  10K
     *
     * @param {Object} legend
     * @param {Object} container
     * @param {Array|null} values - Custom values; null → auto
     * @param {Function} classifierSize - scale: category value → bar height px
     * @param {string|null} title
     * @param {number} titlePadding
     * @param {number} barGroupWidth - Individual bar width from map config
     * @param {number} barGroupGap - Gap between bars from map config
     * @param {Object} catColors
     */
    function drawGroupedSizeLegend(legend, container, values, classifierSize, title, titlePadding, barGroupWidth, barGroupGap, catColors) {
        const domain = classifierSize.domain()
        const autoValues =
            legend.sizeLegend?.valueCount === 2
                ? [domain[1], Math.max(domain[0], domain[1] * 0.1)]
                : [domain[1], Math.round((domain[0] + domain[1]) / 2), Math.max(domain[0], domain[1] * 0.1)]
        const legendValues = values || autoValues
        const sortedValues = [...legendValues].sort((a, b) => b - a) // largest first
        const dec = typeof legend.sizeLegend?.labelFormatter === 'function' ? undefined : getMaxDomainPrecision(classifierSize)
        const offsetX = legend.sizeLegend?.offsetX ?? 0

        const bw = barGroupWidth ?? 16
        const gap = legend.sizeLegend?.barGap ?? barGroupGap ?? 0
        const maxBarHeight = classifierSize(sortedValues[0])
        const colors = catColors ? Object.values(catColors) : ['#7f7f7f']

        let y = 0

        if (title) {
            container.append('text').attr('class', 'em-size-legend-title').attr('x', 0).attr('y', y).attr('dominant-baseline', 'hanging').text(title)
            y += legend.titleFontSize + titlePadding
        }

        // Baseline at y + maxBarHeight
        const baseline = y + maxBarHeight

        // Thin baseline rule
        const totalRuleWidth = sortedValues.length * bw + (sortedValues.length - 1) * gap
        container
            .append('line')
            .attr('x1', offsetX)
            .attr('x2', offsetX + totalRuleWidth)
            .attr('y1', baseline)
            .attr('y2', baseline)
            .attr('stroke', '#aaa')
            .attr('stroke-width', 0.8)

        // Labels are only staggered onto multiple rows to dodge overlap - if the bar spacing
        // already gives each label enough horizontal room (estimated from character count, same
        // ~6.5px/char heuristic used for the map's own bar-badge labels), keep them all on one row.
        const formattedLabels = sortedValues.map((val) => formatSizeLabel(val, legend.sizeLegend?.labelFormatter ?? dec))
        const maxLabelWidth = Math.max(...formattedLabels.map((t) => String(t).length * 6.5))
        const labelSpacing = 14
        const needsStagger = bw + gap < maxLabelWidth + 6

        sortedValues.forEach((val, i) => {
            const minHeight = legend.map?.barSettings_?.groupMinHeight ?? 0
            const barH = Math.max(val > 0 ? minHeight : 0, classifierSize(val))
            const x = offsetX + i * (bw + gap)
            const barColor = '#7f7f7f'

            // Bar growing upward from baseline
            container
                .append('rect')
                .attr('x', x)
                .attr('y', baseline - barH)
                .attr('width', bw)
                .attr('height', barH)
                .attr('rx', legend.map?.barSettings_?.cornerRadius ?? 1)
                .attr('ry', legend.map?.barSettings_?.cornerRadius ?? 1)
                .attr('fill', barColor)
                .attr('stroke', legend.map?.barSettings_?.strokeFill || 'white')
                .attr('stroke-width', (legend.map?.barSettings_?.strokeWidth ?? 0.3) + 'px')

            const labelY = baseline + 12 + (needsStagger ? i * labelSpacing : 0)

            // Vertical tick line from baseline to label
            container
                .append('line')
                .attr('x1', x + bw / 2)
                .attr('x2', x + bw / 2)
                .attr('y1', baseline)
                .attr('y2', labelY - 5)
                .attr('stroke', '#ccc')
                .attr('stroke-width', 0.5)

            container
                .append('text')
                .attr('class', 'em-legend-label em-bar-grouped-legend-label')
                .attr('x', x + bw / 2)
                .attr('y', labelY)
                .attr('text-anchor', 'middle')
                .attr('dominant-baseline', 'middle')
                .text(formattedLabels[i])
        })
    }

    // ── Colour legend ─────────────────────────────────────────────────────────

    function buildColorLegend(out, baseX, baseY) {
        const cfg = out.colorLegend
        if (cfg === false) return

        out._colorLegendContainer = out.lgg.append('g').attr('class', 'em-bar-color-legend')

        const sizeLegendHeight = getLegendBlockHeight(out._sizeLegendContainer)
        const widthLegendHeight = getLegendBlockHeight(out._widthLegendContainer)
        const widthLegendMargin = widthLegendHeight > 0 ? out.widthLegend?.marginTop || 0 : 0
        const y =
            baseY + sizeLegendHeight + widthLegendMargin + widthLegendHeight + (sizeLegendHeight > 0 || widthLegendHeight > 0 ? cfg.marginTop : 0)
        out._colorLegendContainer.attr('transform', `translate(${baseX}, ${y})`)

        if (cfg.title) {
            out._colorLegendContainer.append('text').attr('class', 'em-color-legend-title').attr('x', 0).attr('y', out.titleFontSize).text(cfg.title)
        }

        const scs = map.catColors()
        let i = 0

        for (const code in scs) {
            const y = cfg.titlePadding + (cfg.title ? out.titleFontSize : 0) + i * (cfg.shapeHeight + cfg.shapePadding)

            out._colorLegendContainer
                .append('rect')
                .attr('class', 'em-legend-rect')
                .attr('x', 0)
                .attr('y', y)
                .attr('width', cfg.shapeWidth)
                .attr('height', cfg.shapeHeight)
                .attr('rx', 2)
                .attr('ry', 2)
                .style('fill', scs[code])
                .on('mouseover', function () {
                    highlightRegions(map, code)
                    if (map.insetTemplates_) executeForAllInsets(map.insetTemplates_, map.svgId, highlightRegions, code)
                })
                .on('mouseout', function () {
                    unhighlightRegions(map)
                    if (map.insetTemplates_) executeForAllInsets(map.insetTemplates_, map.svgId, unhighlightRegions, code)
                })

            out._colorLegendContainer
                .append('text')
                .attr('class', 'em-legend-label')
                .attr('x', cfg.shapeWidth + cfg.labelOffsets.x)
                .attr('y', y + cfg.shapeHeight * 0.5)
                .attr('dy', '0.35em')
                .text(map.catLabels()[code] || code)

            i++
        }

        // No-data swatch
        if (cfg.noData) {
            const y = cfg.titlePadding + (cfg.title ? out.titleFontSize : 0) + i * (cfg.shapeHeight + cfg.shapePadding) + out.boxPadding

            const container = out._colorLegendContainer.append('g').attr('class', 'em-no-data-legend').attr('transform', `translate(0, ${y})`)

            out.appendNoDataLegend(container, out.noDataText, highlightRegions, unhighlightRegions)
        }
    }

    function getLegendBlockHeight(container) {
        return container?.node ? container.node()?.getBBox?.().height || 0 : 0
    }

    // ── Highlight helpers ─────────────────────────────────────────────────────

    /**
     * Dim all bar segments then re-highlight the hovered category.
     * Works for both stacked and grouped modes since both use rect[code].
     */
    function highlightRegions(map, code) {
        const allSegments = map.svg_.selectAll('.barchart').selectAll('rect[code]')
        allSegments.style('opacity', 0.15)
        allSegments.filter(`rect[code='${code}']`).style('opacity', 1)
    }

    function unhighlightRegions(map) {
        map.svg_.selectAll('.barchart').selectAll('rect[code]').style('opacity', 1)
    }

    return out
}
